import { Form, FormikProvider } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Switch } from '@antimatterhq/ui/components/switch';
import { InfoTooltip } from '@antimatterhq/ui/components/infoTooltip';
import { AxiosError } from 'axios';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { RuleProcessingScheduleSection } from './forms/RuleProcessingSchedule';
import { RuleInputSection } from './forms/Ruleinput';
import { ObservablesSection } from './forms/Observables';
import { NotableOutputSection } from './forms/NotableOutput';
import { CollationOptionsSection } from './forms/CollationOptions';
import { MitreInformationSection } from './forms/MitreInformation';
import { RuleMetadataSection } from './forms/RuleMetadata';
import { useFormikWithAsyncValidation } from '@/hooks/form/useFormikWithAsyncValidation';
import { TwoColumnLayout } from '@/components/container';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import {
  AddDetectionRuleSchema,
  AddDetectionRuleSchemaValues,
} from '@/zod/schemas/detectionRules/AddDetectionRule';
import { observablesTooltip } from '@/strings/StringsTooltips';
import { ResponseError } from '@/components/responseError/ResponseError';
import { useDebounceValidation } from '@/hooks/formik/useDebounceValidation';
interface DetectionRulesFormProps {
  isInEditMode?: boolean;
  responseError?: AxiosError<{ message: string }>;
  initialValues: AddDetectionRuleSchemaValues;
  onSubmit: (values: AddDetectionRuleSchemaValues) => void;
}

export const DetectionRulesForm = ({
  isInEditMode = false,
  responseError,
  initialValues,
  onSubmit,
}: DetectionRulesFormProps) => {
  const formik = useFormikWithAsyncValidation({
    initialValues,
    validate: toFormikValidate(AddDetectionRuleSchema),
    validationSchema: toFormikValidationSchema(AddDetectionRuleSchema),
    validateOnChange: false,
    onSubmit: async (values) => {
      // Call the original onSubmit
      onSubmit(values);
    },
  });
  useDebounceValidation(formik);

  const { values, setFieldValue, isSubmitting } = formik;
  const onHandleEnableRuleChange = (value: boolean) => {
    setFieldValue('enabled', value);
  };
  const navigate = useNavigate();
  const { enableCollation } = useFlags<FlagSet>();

  return (
    <TableOfContentsProvider>
      <TwoColumnLayout.Root>
        <TwoColumnLayout.LeftColumn>
          <FormikProvider value={formik}>
            <Form>
              <div className="pr-8">
                <TocWrapper name="rule-metadata" displayName="Rule metadata">
                  <Text size="2xl" bold className="mb-4">
                    Create new rule
                  </Text>
                  <Spacer size="lg" />
                  <RuleMetadataSection isInEditMode={isInEditMode} />
                </TocWrapper>

                <Spacer size="2xl" />
                <div className="-mr-16 h-px bg-gray-300" />
                <Spacer size="2xl" />

                <TocWrapper
                  name="mitre-information"
                  displayName="Mitre ATT&amp;CK information"
                >
                  <Text size="xl" bold>
                    Mitre ATT&amp;CK information
                  </Text>
                  <Spacer size="lg" />
                  <MitreInformationSection />
                </TocWrapper>

                <Spacer size="2xl" />
                <div className="-mr-16 h-px bg-gray-300" />
                <Spacer size="2xl" />

                <TocWrapper
                  name="rule-processing-schedule"
                  displayName="Rule processing schedule"
                >
                  <RuleProcessingScheduleSection />
                </TocWrapper>

                <Spacer size="2xl" />
                <div className="-mr-16 h-px bg-gray-300" />
                <Spacer size="2xl" />

                <TocWrapper name="enable-rule" displayName="Enable rule">
                  <Text size="base" bold>
                    Enable rule
                  </Text>
                  <Spacer size="lg" />
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={values.enabled}
                      onCheckedChange={onHandleEnableRuleChange}
                      data-testid="enabled-switch"
                    />
                    <Text size="sm">Enabled</Text>
                  </div>
                </TocWrapper>

                <Spacer size="2xl" />
                <div className="-mr-16 h-px bg-gray-300" />
                <Spacer size="2xl" />

                <TocWrapper name="rule-input" displayName="Rule input">
                  <Text size="xl" bold>
                    Rule input
                  </Text>
                  <Spacer size="lg" />
                  <RuleInputSection />
                </TocWrapper>

                <Spacer size="2xl" />
                <div className="-mr-16 h-px bg-gray-300" />
                <Spacer size="2xl" />

                <TocWrapper name="observables" displayName="Observables">
                  <div className="flex items-center gap-2">
                    <Text size="xl" bold>
                      Observables
                    </Text>
                    <InfoTooltip content={observablesTooltip} />
                  </div>
                  <Spacer size="lg" />
                  <ObservablesSection />
                </TocWrapper>

                <Spacer size="2xl" />
                <div className="-mr-16 h-px bg-gray-300" />
                <Spacer size="2xl" />

                <TocWrapper name="notable-output" displayName="Notable output">
                  <NotableOutputSection />
                </TocWrapper>
                <Spacer size="lg" />
                {enableCollation && (
                  <TocWrapper
                    name="collation-options"
                    displayName="Collation options"
                  >
                    <CollationOptionsSection />
                  </TocWrapper>
                )}
              </div>

              <Spacer size="lg" />

              <ResponseError error={responseError} />

              <div className="flex justify-end gap-2">
                <Button
                  variant="default"
                  onClick={() => navigate('/configure/detection-rules')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={formik.submitDisabled}
                  loading={isSubmitting}
                  data-testid="save-detection-rule-button"
                >
                  {!isInEditMode && 'Create rule'}
                  {isInEditMode && 'Apply changes'}
                </Button>
              </div>
            </Form>
          </FormikProvider>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <TableOfContents className="sticky top-[48px]" />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </TableOfContentsProvider>
  );
};
